/* eslint-disable @reatom/unit-naming-rule */
import { action, atom } from '@reatom/framework'
import i18next from 'i18next'
import { match } from 'ts-pattern'

import { Result } from '@blade/std-lib'
import { userReceivedAction } from '@root/entities/auth/atoms'
import { reatomRequest, tryOrFailSilently } from '@root/shared/lib'
import { showErrorSnackbarAction } from '@root/shared/ui/error-snackbar'
import { showSnackbarAction } from '@root/shared/ui-kit'

import {
  OtpRequiredError,
  UserNotFoundError,
  WrongCredentialsError,
  WrongOtpError,
} from '../../auth-errors'

import { loginRequestAction } from './auth-requests'
import { USERNAME_KEY } from './constants'

import type { LoginRequest } from './auth-requests'
import type { Redacted } from '@blade/std-lib'
import type { OAuthCode, TelegramAuthToken, User } from '@root/entities/auth'
import type { Error } from '@root/shared'

const storedUsername = Result.try({
  try: () => localStorage.getItem(USERNAME_KEY) ?? undefined,
  catch: () => undefined,
}).pipe(Result.merge)

export const storedUsernameAtom = atom(storedUsername, 'storedUsernameAtom')

export type LoginMethod =
  | {
      type: 'telegram'
      token: TelegramAuthToken
      code: OAuthCode
    }
  | {
      type: 'google'
      code: OAuthCode
    }
  | {
      type: 'twitter'
      code: OAuthCode
    }
  | {
      type: 'username'
    }

export type LoginPayload =
  | {
      type: 'telegram'
      token: TelegramAuthToken
      code: OAuthCode
      otp: string | undefined
    }
  | {
      type: 'google'
      code: OAuthCode
      otp: string | undefined
    }
  | {
      type: 'twitter'
      code: OAuthCode
      otp: string | undefined
    }
  | {
      type: 'username'
      username: string
      otp: string | undefined
      password: Redacted.Redacted
    }

export const reatomLogin = () => {
  const payloadAtom = atom<LoginPayload | undefined>(undefined, 'payloadAtom')
  const handleLoginResultAction = action(
    (
      ctx,
      {
        result,
      }: {
        result: Result.Result<
          User.User,
          | Error.UnknownError
          | WrongOtpError
          | OtpRequiredError
          | UserNotFoundError
          | WrongCredentialsError
        >
      },
    ) => {
      if (Result.isSuccess(result)) {
        tryOrFailSilently(() => {
          localStorage.setItem(USERNAME_KEY, result.success.username)
        })
        userReceivedAction(ctx, result.success)
        return
      }

      if (result.failure instanceof OtpRequiredError) {
        const payload = ctx.get(payloadAtom)
        if (!payload) {
          return
        }
        otpRequiredEvent(ctx, payload)
        return
      }

      if (
        result.failure instanceof WrongCredentialsError ||
        result.failure instanceof UserNotFoundError
      ) {
        showSnackbarAction(ctx, {
          text: i18next.t('auth.wrong-credentials'),
          intent: 'error',
        })
        return
      }

      if (result.failure instanceof WrongOtpError) {
        showSnackbarAction(ctx, {
          text: i18next.t('auth.invalid-otp'),
          intent: 'error',
        })
        return
      }

      showErrorSnackbarAction(ctx, result.failure)
    },
    'handleLoginResultAction',
  )

  const loginInternalAction = action(async (ctx, payload: LoginPayload) => {
    const params = match(payload)
      .returnType<LoginRequest>()
      .with({ type: 'google' }, ({ code, otp }) => ({
        password: undefined,
        username: undefined,
        googleAuthCode: code,
        otp,
      }))
      .with(
        { type: 'twitter' },
        ({ code, otp }): LoginRequest => ({
          password: undefined,
          username: undefined,
          twitter: {
            authCode: code,
          },
          otp,
        }),
      )
      .with({ type: 'telegram' }, ({ token, code, otp }) => ({
        password: undefined,
        username: undefined,
        tg: {
          token: token,
          authCode: code,
        },
        otp,
      }))
      .with({ type: 'username' }, ({ password, username, otp }) => ({
        password,
        username,
        otp,
      }))
      .exhaustive()

    payloadAtom(ctx, payload)

    const loginResult = await ctx.schedule(() =>
      loginRequestAction(ctx, params),
    )
    if (Result.isFailure(loginResult)) {
      return loginResult
    }

    return loginResult
  }, 'loginInternalAction')

  const loginRequestState = reatomRequest({
    execAction: loginInternalAction,
    name: 'loginRequest',
    onResult: (ctx, result) => {
      handleLoginResultAction(ctx, { result })
    },
  })

  return { loginRequestState, loginAction: loginRequestState.execute }
}

export const loginModel = reatomLogin()
export const otpRequiredEvent = action<LoginPayload>('otpRequiredEvent')
